import { scopeProjectRef } from "@praxa/client-runtime/environment";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpIcon, FolderPlusIcon, LinkIcon, RotateCcwIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { openCommandPalette } from "../commandPaletteBus";
import { PraxaPlusIcon } from "../components/PraxaPlusIcon";
import { PraxaComposerLogo } from "../components/chat/PraxaComposerLogo";
import { sortScopedProjectsForSidebar } from "../components/Sidebar.logic";
import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { SidebarInset } from "../components/ui/sidebar";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import {
  useAllEnvironmentShellsBootstrapped,
  useProjects,
  useThreadShells,
} from "../state/entities";
import { useEnvironments } from "../state/environments";
import { APP_DISPLAY_NAME } from "~/branding";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

function ChatIndexRouteView() {
  const { authGateState } = Route.useRouteContext();
  const { environments } = useEnvironments();

  if (authGateState.status === "hosted-static" && environments.length === 0) {
    return <HostedStaticOnboardingState />;
  }

  return <IndexDraftLanding />;
}

/**
 * Landing on the index route drops straight into a draft thread for the most
 * recently active project, so the first screen is a prompt instead of a dead
 * end. Falls back to a composer-first project setup state when no project
 * exists yet.
 */
function IndexDraftLanding() {
  const projects = useProjects();
  const threads = useThreadShells();
  const bootstrapped = useAllEnvironmentShellsBootstrapped();
  const handleNewThread = useNewThreadHandler();
  const startingRef = useRef(false);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [startState, setStartState] = useState({ failed: false, retryRequest: 0 });

  const mostRecentProject = useMemo(
    () =>
      bootstrapped
        ? (sortScopedProjectsForSidebar(projects, threads, "updated_at")[0] ?? null)
        : null,
    [bootstrapped, projects, threads],
  );

  useEffect(() => {
    if (mostRecentProject === null || startingRef.current) {
      return;
    }
    startingRef.current = true;
    void handleNewThread(scopeProjectRef(mostRecentProject.environmentId, mostRecentProject.id), {
      initialPrompt: pendingPrompt,
      replace: true,
    }).catch(() => {
      startingRef.current = false;
      setStartState((state) => ({ ...state, failed: true }));
    });
  }, [handleNewThread, mostRecentProject, pendingPrompt, startState.retryRequest]);

  if (!bootstrapped) {
    return null;
  }
  if (mostRecentProject !== null) {
    return startState.failed ? (
      <DraftStartError
        onRetry={() => {
          setStartState((state) => ({
            failed: false,
            retryRequest: state.retryRequest + 1,
          }));
        }}
      />
    ) : null;
  }
  return <NoProjectsHero prompt={pendingPrompt} onPromptChange={setPendingPrompt} />;
}

function DraftStartError({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <Empty className="flex-1">
        <EmptyHeader className="max-w-md">
          <h1 className="font-heading text-xl font-semibold text-foreground">
            Couldn’t open a new agent
          </h1>
          <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
            The project is still available. Try opening the agent again.
          </EmptyDescription>
          <div className="mt-5 flex justify-center">
            <Button size="sm" onClick={onRetry}>
              <RotateCcwIcon className="size-4" />
              Try again
            </Button>
          </div>
        </EmptyHeader>
      </Empty>
    </SidebarInset>
  );
}

function NoProjectsHero(props: {
  readonly onPromptChange: (prompt: string) => void;
  readonly prompt: string;
}) {
  const openAddProject = useCallback(
    () =>
      openCommandPalette({
        open: "add-project",
        ...(props.prompt.trim().length === 0 ? {} : { initialPrompt: props.prompt }),
      }),
    [props.prompt],
  );

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <main className="flex min-h-0 flex-1 items-center px-3 py-10 sm:px-6">
          <div className="mx-auto w-full max-w-2xl">
            <h1 className="sr-only">New agent</h1>
            <div className="flex justify-center">
              <PraxaComposerLogo />
            </div>
            <div className="mb-3 flex items-center justify-center text-sm text-muted-foreground">
              <button
                aria-label="Add a project for this agent"
                className="inline-flex min-h-7 cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                onClick={openAddProject}
                type="button"
              >
                <FolderPlusIcon aria-hidden className="size-3.5" />
                Add project
              </button>
            </div>
            <form
              aria-describedby="new-agent-project-requirement"
              className="chat-composer-draft-hero chat-composer-glass-shell relative w-full"
              onSubmit={(event) => {
                event.preventDefault();
                openAddProject();
              }}
            >
              <div className="chat-composer-glass-host relative z-10 w-full rounded-[22px]">
                <div className="relative z-10 flex min-h-40 flex-col rounded-[22px] px-4 pb-3 pt-3">
                  <textarea
                    aria-label="Describe what you want the agent to do"
                    className="min-h-24 w-full flex-1 resize-none bg-transparent text-[16px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/55 sm:text-[14px]"
                    onChange={(event) => props.onPromptChange(event.currentTarget.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                        event.preventDefault();
                        openAddProject();
                      }
                    }}
                    placeholder="Plan or build. / for commands, @ for context, $ for skills"
                    value={props.prompt}
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <Button onClick={openAddProject} size="sm" type="button" variant="ghost">
                      <PraxaPlusIcon className="size-4" />
                      Add project
                    </Button>
                    <Button
                      aria-label="Add a project to continue with this prompt"
                      disabled={props.prompt.trim().length === 0}
                      size="icon-sm"
                      title="Add a project to continue"
                      type="submit"
                    >
                      <ArrowUpIcon className="size-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </form>
            <p
              className="mt-3 text-center text-xs text-muted-foreground/70"
              id="new-agent-project-requirement"
            >
              Choose a project to give the agent a workspace. Your prompt will be preserved.
            </p>
          </div>
        </main>
      </div>
    </SidebarInset>
  );
}

export const Route = createFileRoute("/_chat/")({
  component: ChatIndexRouteView,
});

function HostedStaticOnboardingState() {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <header
          className={cn(
            "border-b border-border px-3 py-2 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5 sm:py-3",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground md:text-muted-foreground/60">
              {APP_DISPLAY_NAME}
            </span>
          </div>
        </header>

        <Empty className="flex-1">
          <div className="w-full max-w-xl rounded-3xl border border-border/55 bg-card/20 px-8 py-12 shadow-sm/5">
            <EmptyHeader className="max-w-none">
              <div className="mx-auto mb-5 flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
                <LinkIcon className="size-5" />
              </div>
              <EmptyTitle className="text-foreground text-xl">
                Connect an environment to get started
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm leading-relaxed text-muted-foreground/78">
                Add a reachable backend manually to start working from this browser.
              </EmptyDescription>
              <div className="mt-6 flex justify-center">
                <Button render={<Link to="/settings/connections" />} size="sm">
                  <PraxaPlusIcon className="size-4" />
                  Add environment
                </Button>
              </div>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}
